// -----------------------------------------------------------------------------
// Renders the attachments hanging off a chat message bubble. Images show
// *only* their inline base64 thumbnail (shipped with the message, so the
// list renders with zero extra fetches) - clicking it opens the full-size
// image in an in-app lightbox and a corner download button saves it
// instead; other file types render as a download chip. Everything goes
// through the member-guarded `/chat/channels/:id/attachments/:id`
// endpoint, so the blobs are never publicly addressable and full-size
// files only transfer when a user explicitly asks for one.
// -----------------------------------------------------------------------------

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowDownTrayIcon, DocumentIcon } from "@heroicons/react/24/outline";

import classNames from "../../utils/classNames";
import { IChatAttachment } from "../../models/Chat";
import { useChatAttachmentDownload } from "../../data/useChat";
import ChatImageLightbox from "./ChatImageLightbox";

interface ChatMessageAttachmentsProps {
  channelId: string;
  attachments: IChatAttachment[];
  /** True when shown inside the current user's own (primary-filled) bubble. */
  onPrimary: boolean;
}

export default function ChatMessageAttachments({
  channelId,
  attachments,
  onPrimary,
}: ChatMessageAttachmentsProps) {
  const { t } = useTranslation("chat");
  const download = useChatAttachmentDownload();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [lightbox, setLightbox] = useState<{
    url: string;
    attachment: IChatAttachment;
  } | null>(null);

  // Revoke the lightbox object URL when it closes / swaps.
  useEffect(() => {
    if (!lightbox) return;
    return () => URL.revokeObjectURL(lightbox.url);
  }, [lightbox]);

  const fetchBlob = async (attachment: IChatAttachment): Promise<Blob | null> => {
    if (busyId) return null;
    setBusyId(attachment.id);
    try {
      return await download.mutateAsync({
        channelId,
        attachmentId: attachment.id,
      });
    } catch {
      return null;
    } finally {
      setBusyId(null);
    }
  };

  const handleOpenImage = async (attachment: IChatAttachment) => {
    const blob = await fetchBlob(attachment);
    if (!blob) return;
    setLightbox({ url: URL.createObjectURL(blob), attachment });
  };

  const saveBlob = (blob: Blob, fileName: string) => {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = fileName;
    anchor.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };

  const handleDownloadFile = async (attachment: IChatAttachment) => {
    const blob = await fetchBlob(attachment);
    if (!blob) return;
    saveBlob(blob, attachment.fileName);
  };

  const handleDownloadFromLightbox = async () => {
    if (!lightbox) return;
    // The full-size bytes are already on hand as the object URL - no
    // second fetch needed.
    const blob = await fetch(lightbox.url).then((r) => r.blob());
    saveBlob(blob, lightbox.attachment.fileName);
  };

  return (
    <div className="mt-1.5 flex flex-wrap gap-2">
      {attachments.map((attachment) => {
        const isImage = attachment.contentType?.startsWith("image/");
        const isBusy = busyId === attachment.id;

        if (isImage && attachment.thumbnail) {
          return (
            <div
              key={attachment.id}
              className={classNames(
                "relative overflow-hidden rounded-lg border",
                onPrimary ? "border-white/30" : "border-gray-200",
                isBusy ? "opacity-60" : ""
              )}
            >
              <button
                type="button"
                onClick={() => handleOpenImage(attachment)}
                disabled={isBusy}
                title={t("Open {{name}}", { name: attachment.fileName })}
                className={classNames("block", isBusy ? "" : "hover:opacity-90")}
              >
                <img
                  src={`data:image/png;base64,${attachment.thumbnail}`}
                  alt={attachment.fileName}
                  className="max-h-36 min-h-[3rem] min-w-[3rem] object-cover"
                />
              </button>
              <button
                type="button"
                onClick={() => handleDownloadFile(attachment)}
                disabled={isBusy}
                title={t("Download {{name}} ({{size}})", {
                  name: attachment.fileName,
                  size: formatFileSize(attachment.length),
                })}
                className="absolute bottom-1.5 right-1.5 flex h-7 w-7 items-center justify-center rounded-full bg-black/55 text-white shadow hover:bg-black/75 disabled:opacity-60"
              >
                <ArrowDownTrayIcon className="h-4 w-4" />
              </button>
            </div>
          );
        }

        return (
          <button
            key={attachment.id}
            type="button"
            onClick={() =>
              isImage ? handleOpenImage(attachment) : handleDownloadFile(attachment)
            }
            title={
              isImage
                ? t("Open {{name}}", { name: attachment.fileName })
                : t("Download {{name}}", { name: attachment.fileName })
            }
            className={classNames(
              "flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-left text-xs",
              onPrimary
                ? "border-white/30 bg-white/10 text-white hover:bg-white/20"
                : "border-gray-200 bg-gray-50 text-gray-700 hover:bg-gray-100",
              isBusy ? "opacity-60" : ""
            )}
          >
            <DocumentIcon className="h-4 w-4 shrink-0" />
            <span className="max-w-[11rem] truncate font-medium">
              {attachment.fileName}
            </span>
            <span className={onPrimary ? "text-white/70" : "text-gray-400"}>
              {formatFileSize(attachment.length)}
            </span>
            <ArrowDownTrayIcon className="h-3.5 w-3.5 shrink-0 opacity-70" />
          </button>
        );
      })}

      {lightbox ? (
        <ChatImageLightbox
          url={lightbox.url}
          fileName={lightbox.attachment.fileName}
          onDownload={handleDownloadFromLightbox}
          onClose={() => setLightbox(null)}
        />
      ) : null}
    </div>
  );
}

function formatFileSize(bytes: number): string {
  if (!bytes || bytes <= 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
